import React, { createContext, useCallback, useContext, useState } from 'react';
import { useSyncOutside } from 'shared/hooks';

// Define the Page type for TypeScript
type Page = {
    PageID: number;
    PageName: string;
};

function safeJSONParse(item: string | null) {
    if (item === undefined || item === null) {
        return null; // or a sensible default
    }
    try {
        return JSON.parse(item);
    } catch (error) {
        return null; // or return a sensible default depending on your application's needs
    }
}

export const ActivePageContext = createContext<{
    activeReportId: string | null;
    setActiveReportId: (reportId: string | null) => void;
    activePage: number | null;
    setActivePage: (pageId: number | null) => void;
    pages: Page[];
    setPages: (pages: Page[]) => void;
    addPage: (page: Page) => void;
    deletePage: (pageId: number) => void;
}>({
    activeReportId: null,
    setActiveReportId: () => { },
    activePage: null,
    setActivePage: () => { },
    pages: [],
    setPages: () => { },
    addPage: () => { },
    deletePage: () => { },
});

export const ActivePageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // The active report and page survive a reload of the tab: each choice is stored as it is made.
    const [activeReportId, setActiveReportIdState] = useState<string | null>(() => {
        return sessionStorage.getItem('reportId');
    });
    const setActiveReportId = useCallback((reportId: string | null) => {
        if (reportId !== null) {
            sessionStorage.setItem('reportId', reportId);
        }
        setActiveReportIdState(reportId);
    }, []);

    const [activePage, setActivePageState] = useState<number | null>(() => {
        const savedPageId = sessionStorage.getItem('activePageId');
        return savedPageId ? safeJSONParse(savedPageId) : null;
    });
    const setActivePage = useCallback((pageId: number | null) => {
        sessionStorage.setItem('activePageId', JSON.stringify(pageId));
        setActivePageState(pageId);
    }, []);

    const [pages, setPages] = useState<Page[]>(() => {
        const savedPages = sessionStorage.getItem('pages');
        return savedPages ? safeJSONParse(savedPages) : [];
    });

    useSyncOutside(() => {
        sessionStorage.setItem('pages', JSON.stringify(pages));
    }, [pages]);

    const addPage = (newPage: Page) => {
        setPages((prevPages) => [...prevPages, newPage]);
    };

    const deletePage = (pageId: number) => {
        setPages((prevPages) => prevPages.filter(page => page.PageID !== pageId));
        if (activePage === pageId) {
            setActivePage(null); // Reset active page if the deleted page was active
        }
    };

    return (
        <ActivePageContext.Provider value={{
            activeReportId,
            setActiveReportId,
            activePage,
            setActivePage,
            pages,
            setPages,
            addPage,
            deletePage
        }}>
            {children}
        </ActivePageContext.Provider>
    );
};

export const useActivePage = () => useContext(ActivePageContext);